import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Accessibility, X, RotateCcw, FileText } from "lucide-react";
import { Switch } from "@/components/ui/switch";

const STORAGE_KEY = "vbs-a11y-settings";

const DEFAULTS = {
  fontSize: "normal", // normal | lg | xl
  contrast: "default", // default | dark | light | grayscale
  links: false,
  dyslexia: false,
  cursor: false,
  motion: false,
};

const applySettings = (s) => {
  const el = document.documentElement;
  el.classList.toggle("a11y-font-lg", s.fontSize === "lg");
  el.classList.toggle("a11y-font-xl", s.fontSize === "xl");
  el.classList.toggle("a11y-contrast-dark", s.contrast === "dark");
  el.classList.toggle("a11y-contrast-light", s.contrast === "light");
  el.classList.toggle("a11y-grayscale", s.contrast === "grayscale");
  el.classList.toggle("a11y-links", s.links);
  el.classList.toggle("a11y-dyslexia", s.dyslexia);
  el.classList.toggle("a11y-cursor", s.cursor);
  el.classList.toggle("a11y-motion", s.motion);
};

const AccessibilityWidget = () => {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(() => {
    try {
      return { ...DEFAULTS, ...JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}") };
    } catch {
      return DEFAULTS;
    }
  });

  useEffect(() => {
    applySettings(settings);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  }, [settings]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const set = (patch) => setSettings((s) => ({ ...s, ...patch }));

  const fontOptions = [
    { value: "normal", label: "A", hint: "Default text size", testId: "a11y-font-normal" },
    { value: "lg", label: "A+", hint: "Large text (+15%)", testId: "a11y-font-large" },
    { value: "xl", label: "A++", hint: "Extra large text (+30%)", testId: "a11y-font-xl" },
  ];

  const contrastOptions = [
    { value: "default", label: "Default", testId: "a11y-contrast-default" },
    { value: "dark", label: "Dark Contrast", testId: "a11y-contrast-dark" },
    { value: "light", label: "Light Contrast", testId: "a11y-contrast-light" },
    { value: "grayscale", label: "Grayscale", testId: "a11y-contrast-grayscale" },
  ];

  const toggles = [
    { key: "links", label: "Highlight Links", hint: "Underline and outline all links", testId: "a11y-toggle-links" },
    { key: "dyslexia", label: "Dyslexia Friendly Font", hint: "Atkinson Hyperlegible typeface", testId: "a11y-toggle-dyslexia" },
    { key: "cursor", label: "Big Cursor", hint: "Enlarged high-contrast pointer", testId: "a11y-toggle-cursor" },
    { key: "motion", label: "Reduce Motion", hint: "Disable animations and smooth scrolling", testId: "a11y-toggle-motion" },
  ];

  return createPortal(
    <div className="fixed bottom-6 left-6 z-[90] font-sans" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {open && (
        <div
          data-testid="a11y-panel"
          role="dialog"
          aria-label="Accessibility options"
          className="mb-4 w-[320px] max-w-[calc(100vw-3rem)] rounded-xl bg-white shadow-2xl shadow-slate-900/25 border border-slate-200 overflow-hidden"
        >
          <div className="flex items-center justify-between bg-[#00203d] px-5 py-4">
            <div className="flex items-center gap-2.5 text-white">
              <Accessibility className="h-5 w-5 text-amber-500" aria-hidden="true" />
              <div>
                <p className="font-heading font-bold text-sm leading-tight">Accessibility Options</p>
                <p className="text-[11px] text-slate-300">WCAG 2.1 Level AA tools</p>
              </div>
            </div>
            <button
              type="button"
              data-testid="a11y-close-button"
              aria-label="Close accessibility options"
              onClick={() => setOpen(false)}
              className="text-slate-300 hover:text-white p-1 rounded-md"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="p-5 space-y-5 max-h-[60vh] overflow-y-auto">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-2.5">Text Size</p>
              <div className="grid grid-cols-3 gap-2">
                {fontOptions.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    data-testid={o.testId}
                    aria-pressed={settings.fontSize === o.value}
                    title={o.hint}
                    onClick={() => set({ fontSize: o.value })}
                    className={`py-2.5 rounded-lg border text-sm font-bold transition-colors duration-150 ${
                      settings.fontSize === o.value
                        ? "bg-[#00203d] text-white border-[#00203d]"
                        : "bg-white text-slate-700 border-slate-200 hover:border-[#00203d]"
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-2.5">Contrast & Color</p>
              <div className="grid grid-cols-2 gap-2">
                {contrastOptions.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    data-testid={o.testId}
                    aria-pressed={settings.contrast === o.value}
                    onClick={() => set({ contrast: o.value })}
                    className={`py-2.5 px-2 rounded-lg border text-xs font-bold transition-colors duration-150 ${
                      settings.contrast === o.value
                        ? "bg-[#00203d] text-white border-[#00203d]"
                        : "bg-white text-slate-700 border-slate-200 hover:border-[#00203d]"
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-3.5">
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Reading & Navigation</p>
              {toggles.map((t) => (
                <div key={t.key} className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{t.label}</p>
                    <p className="text-[11px] text-slate-500">{t.hint}</p>
                  </div>
                  <Switch
                    data-testid={t.testId}
                    checked={settings[t.key]}
                    onCheckedChange={(v) => set({ [t.key]: v })}
                    aria-label={t.label}
                  />
                </div>
              ))}
            </div>

            <div className="pt-1 space-y-2.5 border-t border-slate-100">
              <Link
                to="/accessibility-statement"
                data-testid="a11y-statement-link"
                onClick={() => setOpen(false)}
                className="flex items-center gap-2 text-sm font-bold text-[#00203d] hover:text-amber-600 pt-3 transition-colors duration-150"
              >
                <FileText className="h-4 w-4" aria-hidden="true" />
                Read our Accessibility Statement
              </Link>
              <button
                type="button"
                data-testid="a11y-reset-button"
                onClick={() => setSettings(DEFAULTS)}
                className="flex items-center gap-2 text-xs font-semibold text-slate-500 hover:text-slate-800 transition-colors duration-150"
              >
                <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
                Reset all settings
              </button>
            </div>
          </div>
        </div>
      )}

      <button
        type="button"
        data-testid="a11y-widget-trigger"
        aria-label={open ? "Close accessibility options" : "Open accessibility options"}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="h-14 w-14 rounded-full bg-[#0284c7] hover:bg-[#0369a1] text-white shadow-xl shadow-sky-900/30 flex items-center justify-center transition-transform duration-200 hover:scale-105 focus:outline-none focus:ring-4 focus:ring-amber-500/60"
      >
        <Accessibility className="h-7 w-7" aria-hidden="true" />
      </button>
    </div>,
    document.body
  );
};

export default AccessibilityWidget;
